import { z } from 'zod';
import { InvoiceKindSchema, InvoiceStatusSchema } from './billing';
// ---------- B2 Contabilidad ligera: libros e informes para la gestoría ----------
//
// Toga NO lleva la contabilidad del despacho: la lleva su gestoría. Esto es lo que se le
// pasa (libro de facturas expedidas, IVA repercutido por trimestre) y lo que el despacho
// necesita para cobrar (cartera con antigüedad) y para sus clientes (provisiones y
// suplidos). Son informes orientativos, no modelos oficiales.
export const LedgerQuerySchema = z.object({
    issuerId: z.string().min(1).optional(),
    clientId: z.string().min(1).optional(),
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    quarter: z.coerce.number().int().min(1).max(4).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
    /** `no` = solo las que aún no se han pasado a la gestoría. */
    accounted: z.enum(['all', 'yes', 'no']).default('all'),
});
export const LedgerVatLineSchema = z.object({
    /** Tipo de IVA como fracción («0.2100»). */
    rate: z.string(),
    baseCents: z.number().int(),
    vatCents: z.number().int(),
});
export const LedgerRowSchema = z.object({
    id: z.string(),
    number: z.string(),
    issueDate: z.string(),
    issuerId: z.string(),
    issuerName: z.string(),
    issuerNif: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    clientNif: z.string().nullable(),
    kind: InvoiceKindSchema,
    status: InvoiceStatusSchema,
    rectifiesNumber: z.string().nullable(),
    /** Número de la rectificativa por sustitución emitida que la reemplaza, si la hay. */
    replacedByNumber: z.string().nullable(),
    /** Base imponible y cuota por tipo de IVA (sin los suplidos). */
    vat: z.array(LedgerVatLineSchema),
    baseCents: z.number().int(),
    suppliedCents: z.number().int(),
    vatCents: z.number().int(),
    /** Importe de la factura: base + suplidos + IVA (sin restar retención ni provisión). */
    invoiceTotalCents: z.number().int(),
    irpfCents: z.number().int(),
    fundsAppliedCents: z.number().int(),
    /** Lo que el cliente paga: importe − retención − provisión aplicada. */
    payableCents: z.number().int(),
    paidCents: z.number().int(),
    /** Cuenta en los totales (no anulada ni sustituida por otra). */
    counted: z.boolean(),
    accountedAt: z.string().nullable(),
});
export const LedgerTotalsSchema = z.object({
    count: z.number().int(),
    baseCents: z.number().int(),
    suppliedCents: z.number().int(),
    vatCents: z.number().int(),
    invoiceTotalCents: z.number().int(),
    irpfCents: z.number().int(),
    fundsAppliedCents: z.number().int(),
    payableCents: z.number().int(),
    paidCents: z.number().int(),
});
export const LedgerResponseSchema = z.object({
    rows: z.array(LedgerRowSchema),
    /** Solo las facturas que cuentan. */
    totals: LedgerTotalsSchema,
    /** Facturas del listado que no cuentan en los totales (anuladas o sustituidas). */
    uncountedCount: z.number().int(),
    /** Facturas del listado que aún no se han pasado a la gestoría. */
    pendingAccountingCount: z.number().int(),
    /** El listado está recortado (demasiadas facturas): acota el periodo. */
    truncated: z.boolean(),
});
export const VatQuarterSchema = z.object({
    quarter: z.number().int().min(1).max(4),
    byRate: z.array(LedgerVatLineSchema),
    baseCents: z.number().int(),
    vatCents: z.number().int(),
    suppliedCents: z.number().int(),
    irpfCents: z.number().int(),
    invoiceCount: z.number().int(),
});
export const VatSummaryResponseSchema = z.object({
    year: z.number().int(),
    quarters: z.array(VatQuarterSchema),
    total: VatQuarterSchema.omit({ quarter: true }),
});
export const ReceivableRowSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    invoiceCount: z.number().int(),
    notDueCents: z.number().int(),
    late1to30Cents: z.number().int(),
    late31to60Cents: z.number().int(),
    late61to90Cents: z.number().int(),
    lateOver90Cents: z.number().int(),
    totalCents: z.number().int(),
});
export const ReceivablesResponseSchema = z.object({
    asOf: z.string(),
    rows: z.array(ReceivableRowSchema),
    totals: ReceivableRowSchema.omit({ clientId: true, clientName: true }),
});
export const ThirdPartyFundsRowSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    receivedCents: z.number().int(),
    appliedCents: z.number().int(),
    refundedCents: z.number().int(),
    /** Saldo que el despacho debe al cliente (provisión sin aplicar ni devolver). */
    availableCents: z.number().int(),
    /** Suplidos facturados en el periodo elegido. */
    suppliedInvoicedCents: z.number().int(),
});
export const ThirdPartyFundsResponseSchema = z.object({
    year: z.number().int(),
    rows: z.array(ThirdPartyFundsRowSchema),
    totals: ThirdPartyFundsRowSchema.omit({ clientId: true, clientName: true }),
});
export const MarkAccountedSchema = z.object({
    ids: z.array(z.string().min(1)).min(1).max(1000),
    accounted: z.boolean(),
});
//# sourceMappingURL=accounting.js.map